import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { currency, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Download, FileCheck2, Calendar, ShieldCheck, ShieldAlert, MapPin, IndianRupee, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    processing: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    disbursed: "bg-komma-gold/15 text-komma-navy border-komma-gold/40",
    rejected: "bg-red-50 text-red-700 border-red-200",
    closed: "bg-gray-100 text-gray-700 border-gray-200",
};
const STATUS_OPTIONS = ["new", "processing", "approved", "disbursed", "rejected", "closed"];

export default function LoanDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [payments, setPayments] = useState([]);
    const canApprove = ["ceo", "office_admin", "agent", "branch_manager"].includes(user.role);
    const canRecordPayment = ["ceo", "office_admin", "branch_manager", "agent"].includes(user.role);

    const load = useCallback(async () => {
        try {
            const [detail, pay] = await Promise.all([
                api.get(`/loans/${id}`),
                api.get(`/payments?loan_id=${id}`).catch(() => ({ data: [] })),
            ]);
            setData(detail.data);
            setPayments(pay.data);
        }
        catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const updateStatus = async (status) => {
        try { await api.patch(`/loans/${id}/status`, { status }); toast.success(`Loan ${status}`); load(); }
        catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const markPaid = async (paymentId) => {
        try { await api.post(`/payments/${paymentId}/mark-paid`); toast.success("Payment recorded"); load(); }
        catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    const downloadPdf = async () => {
        try {
            const res = await api.get(`/loans/${id}/sanction-letter`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Sanction-${data?.loan?.loan_number || id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error("Could not generate PDF"); }
    };

    if (!data) return <div className="text-gray-500">Loading loan…</div>;
    const { loan, customer, documents, visits, branch } = data;

    return (
        <div className="space-y-6" data-testid="loan-detail-page">
            <Link to="/loans" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-komma-navy hover:text-komma-gold" data-testid="back-to-loans">
                <ChevronLeft size={14} /> Back to Loans
            </Link>

            <div className="komma-card p-8 relative overflow-hidden">
                <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-komma-gold/10" />
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 relative">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-komma-gold" data-testid="loan-number">{loan.loan_number}</p>
                        <h1 className="font-serif text-3xl text-komma-navy mt-2">{loan.customer_name}</h1>
                        <p className="text-sm text-gray-600 capitalize">{loan.loan_type} loan · {branch?.name || "—"}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Badge variant="outline" className={`${STATUS_STYLES[loan.status]} capitalize`}>{loan.status}</Badge>
                        <Button onClick={downloadPdf} className="bg-komma-gold hover:bg-komma-gold-dark text-komma-navy" data-testid="download-pdf-btn">
                            <Download size={14} className="mr-2" /> Sanction Letter
                        </Button>
                    </div>
                </div>
                <div className="komma-gold-hairline my-6" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatBlock label="Amount" value={currency(loan.amount)} />
                    <StatBlock label="EMI" value={currency(loan.emi)} />
                    <StatBlock label="Tenure" value={`${loan.tenure_months} mo`} />
                    <StatBlock label="Interest" value={`${loan.interest_rate}% p.a.`} />
                </div>
                {canApprove && (
                    <div className="mt-6 flex items-center gap-3">
                        <span className="text-xs uppercase tracking-widest text-gray-500">Change status:</span>
                        <Select value={loan.status} onValueChange={updateStatus}>
                            <SelectTrigger className="h-9 w-52" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="komma-card p-6" data-testid="documents-panel">
                        <div className="flex items-center gap-2">
                            <FileCheck2 size={18} className="text-komma-gold" />
                            <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">Documents</p>
                        </div>
                        <div className="komma-gold-hairline my-4" />
                        {documents.length === 0 ? (
                            <p className="text-sm text-gray-500">No documents attached. Upload from the Documents page.</p>
                        ) : (
                            <div className="space-y-2">
                                {documents.map((d) => (
                                    <div key={d.id} className="flex items-center justify-between p-3 rounded-md border border-gray-100 hover:border-komma-gold/40 transition-colors">
                                        <div>
                                            <p className="text-sm font-semibold text-komma-navy capitalize">{d.doc_type.replace("_", " ")}</p>
                                            <p className="text-xs text-gray-500">{d.filename} · {new Date(d.created_at).toLocaleDateString()}</p>
                                        </div>
                                        <Badge variant="outline" className={d.is_valid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>
                                            {d.is_valid ? <ShieldCheck size={12} className="mr-1" /> : <ShieldAlert size={12} className="mr-1" />}
                                            {Math.round((d.confidence || 0) * 100)}%
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="komma-card p-6" data-testid="visits-panel">
                        <div className="flex items-center gap-2">
                            <MapPin size={18} className="text-komma-gold" />
                            <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">Field Visits</p>
                        </div>
                        <div className="komma-gold-hairline my-4" />
                        {visits.length === 0 ? (
                            <p className="text-sm text-gray-500">No visits linked to this loan.</p>
                        ) : visits.map((v) => (
                            <div key={v.id} className="flex justify-between py-3 border-b border-gray-100 last:border-0">
                                <div>
                                    <p className="text-sm font-semibold text-komma-navy">{v.purpose}</p>
                                    <p className="text-xs text-gray-500">{new Date(v.scheduled_at).toLocaleString()}</p>
                                </div>
                                <Badge variant="outline" className="capitalize">{v.status}</Badge>
                            </div>
                        ))}
                    </div>

                    {payments.length > 0 && (
                        <div className="komma-card p-6" data-testid="payments-panel">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <IndianRupee size={18} className="text-komma-gold" />
                                    <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">EMI Schedule</p>
                                </div>
                                <span className="text-xs text-gray-500">
                                    {payments.filter((p) => p.paid).length} / {payments.length} paid
                                </span>
                            </div>
                            <div className="komma-gold-hairline my-4" />
                            <div className="overflow-hidden rounded-md border border-gray-100">
                                <table className="w-full text-sm">
                                    <thead className="bg-komma-navy/5">
                                        <tr>
                                            {["#", "Due Date", "Amount", "Status", canRecordPayment ? "Action" : ""].filter(Boolean).map((h) => (
                                                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-komma-navy">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.slice(0, 8).map((p) => {
                                            const overdue = !p.paid && new Date(p.due_date) < new Date();
                                            return (
                                                <tr key={p.id} className="border-t border-gray-100" data-testid={`payment-${p.id}`}>
                                                    <td className="px-3 py-2 text-xs">{p.installment}</td>
                                                    <td className="px-3 py-2 text-xs text-komma-navy">{new Date(p.due_date).toLocaleDateString()}</td>
                                                    <td className="px-3 py-2 text-xs font-semibold">{currency(p.amount)}</td>
                                                    <td className="px-3 py-2">
                                                        {p.paid ? (
                                                            <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> Paid</span>
                                                        ) : overdue ? (
                                                            <span className="text-xs text-red-700">Overdue</span>
                                                        ) : (
                                                            <span className="text-xs text-gray-500">Upcoming</span>
                                                        )}
                                                    </td>
                                                    {canRecordPayment && (
                                                        <td className="px-3 py-2">
                                                            {!p.paid && (
                                                                <Button size="sm" variant="outline" onClick={() => markPaid(p.id)}
                                                                    className="h-7 text-xs border-komma-gold text-komma-navy hover:bg-komma-gold/10"
                                                                    data-testid={`mark-paid-${p.id}`}>
                                                                    Mark paid
                                                                </Button>
                                                            )}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {payments.length > 8 && (
                                <p className="mt-3 text-xs text-gray-500">Showing first 8 of {payments.length} installments.</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="komma-card p-6" data-testid="timeline-panel">
                    <div className="flex items-center gap-2">
                        <Calendar size={18} className="text-komma-gold" />
                        <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">Timeline</p>
                    </div>
                    <div className="komma-gold-hairline my-4" />
                    <ol className="relative border-l border-komma-gold/40 pl-5 space-y-5">
                        {(loan.history || []).slice().reverse().map((h, i) => (
                            <li key={i}>
                                <div className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-komma-gold" />
                                <p className="text-sm font-semibold text-komma-navy capitalize">{h.status}</p>
                                <p className="text-xs text-gray-500">{new Date(h.at).toLocaleString()}</p>
                                <p className="text-xs text-gray-500">by {h.by}</p>
                                {h.remark && <p className="text-xs text-gray-700 mt-1 italic">&ldquo;{h.remark}&rdquo;</p>}
                            </li>
                        ))}
                    </ol>

                    {customer && (
                        <>
                            <div className="komma-gold-hairline my-6" />
                            <p className="text-[10px] uppercase tracking-widest text-gray-500">Borrower</p>
                            <p className="font-serif text-lg text-komma-navy mt-1">{customer.name}</p>
                            <p className="text-xs text-gray-600">{customer.phone}</p>
                            <p className="text-xs text-gray-600">{customer.email}</p>
                            <p className="text-xs text-gray-600 mt-1">{customer.address}</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatBlock({ label, value }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500">{label}</p>
            <p className="font-serif text-xl text-komma-navy mt-1">{value}</p>
        </div>
    );
}
